<template>
  <div class="container-fluid">
    <h1 class="mb-3">Portal User Management</h1>
    
    <div v-if="auth.isAdmin" class="row mb-3">
      
      <div class="col d-flex align-items-center">
        <AddActions view="User" @new="newModal"/>
      </div>
      
      <div class="col col-auto">
        <div v-if="!pendingList.length">
          <span class="small text-muted">
            No pending user requests
          </span>          
        </div>
        <div v-else>
          <button class="btn btn-sm btn-danger" @click="pendingShow = !pendingShow">
            {{ pendingList.length }} Pending Requests
          </button>
        </div>
      </div>
      
      <ToggleLockedButton 
        v-if="auth.isAdmin" 
        v-model="includeLocked" 
        class="me-2"
        @toggle="toggleLockedFilter"
      />

    </div>

    <div v-if="pendingShow && pendingList.length">
      <h4 class="mb-3 text-danger">Pending User Requests</h4>
      <UserTable 
        modelName="Pending Requests" 
        :data="pendingList"  
        :columns="pendingColumns" 
        :options="tableOptions" 
        ref="pendingTable"
      />
    </div>

    <div>
      <UserForm v-if="modalNew || modalEdit || modalDetail"
        :msg="`${modalMsg} User`" :tag="`user`"
        :schema="entitySchema" :schemaTypes="schemaTypes" :parentObj="editUser"
        @formDataSubmitted="handleSave" @formClosed="closedModal"
      />
    </div>
    
    <UserTable 
      modelName="User"   
      :columns="columns" 
      :ajaxUrl="userUrl" 
      :queryParams="userQueryParams" 
      :options="tableOptions" 
      ref="usersTable"
    >
      <template #details="row">
        <TableActions mode="details" :rowData="row.rowData" @detail="detail" />
      </template>
      <template #actions="row">
        <TableActions mode="actions" :rowData="row.rowData" @edit="edit" @remove="remove" />
      </template>
    </UserTable>

  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';
import { ApiEndpoint } from '@/utils/config';
import { getCommonColumns, renderTime, renderExpandableJson } from '@/utils/table';
import { handleRowSave, handleRowDelete } from '@/utils/form';

import UserTable from '@/components/TableComponent.vue';
import UserForm from '@/components/DataFormModal.vue';
import TableActions from '@/components/TableActions.vue';
import AddActions from '@/components/AddActions.vue';
import ToggleLockedButton from '@/components/ToggleLockedButton.vue';
import { apiFetch } from '@/utils/api';

const auth = useAuthStore();
const schema = useSchemaStore();

const usersTable = ref();
const entitySchema = ref([]);
const schemaTypes = reactive({});

const includeLocked = ref(false);

const pendingTable = ref();
const pendingList = ref([]);
const pendingShow = ref(false);

const modalNew = ref(false);
const modalEdit = ref(false);
const modalDetail = ref(false);
const editUser = reactive({});

const modalMsg = computed(() => {
  if (modalNew.value) return 'Add';
  if (modalEdit.value) return 'Edit';
  return 'View';
});

const columns = [
  { title: 'Customer', data: 'customerId' },
  { title: 'Name', data: 'name' },
  { title: 'ID', data: 'id' },
  { title: 'Role', data: 'role' },
  { title: 'Email', data: 'email' },
  { title: 'Last Login', data: 'lastLogin', render: renderTime },
  // { title: 'Auth Provider', data: 'authProvider' },
  // { title: 'Auth Provider ID', data: 'authProviderId'},
  
  ...getCommonColumns(),
];

const pendingColumns = [
  { title: 'Created At', data: 'createdAt', width: '10%', render: renderTime },
  { title: 'Request Detail', data: 'detail', render: renderExpandableJson },
  { title: 'Request ID', data: 'id', width: '15%' },
  { title: 'Notification ID', data: 'notificationId', width: '10%' },
];

const tableOptions = {};

const userUrl = ApiEndpoint.USERS;
const userQueryParams = computed(() => {
  const params = {
    includeLocked: includeLocked.value ? includeLocked.value : undefined,
  };
  return params;
});

const toggleLockedFilter = async () => {
  await loadUsers(true);
};

const loadUsers = async (forceRefresh = false) => {
  await nextTick();
  if (usersTable.value) {
    await usersTable.value.reloadTable(forceRefresh);
  }
};

const loadPendingUsers = async (forceRefresh = false) => {
  const pendingUrl = ApiEndpoint.USERS + '/pending';
  const { items } = await apiFetch(pendingUrl);
  if (Array.isArray(items)) {
    pendingList.value = items;
  }
  await nextTick();
  if (pendingTable.value) {
    await pendingTable.value.reloadTable(forceRefresh);
  }
};

const resetUser = (obj) => {
  Object.keys(obj).forEach(key => delete obj[key]);
};

const newModal = () => {
  resetUser(editUser);
  modalNew.value = true;
};

const handleSave = async (detail) => {
  try {
    await handleRowSave('user', detail, { ...editUser });
    closedModal();
    await loadUsers(true);
    await loadPendingUsers(true);
  } catch (err) {
    console.error(err);
  }
};

const edit = (row) => {
  Object.assign(editUser, row);
  modalEdit.value = true;
};

const detail = (row) => {
  Object.assign(editUser, row);
  modalDetail.value = true;
};

const remove = async (row) => {
  if (row.id === auth.userId) {
    confirm(`Cannot delete/restore self`);
    return;
  }
  const confirmKeys = ['id', 'name', 'customerId'];
  await handleRowDelete('user', row, confirmKeys);
  await loadUsers(true);
}

/** Resets modal values after modal close, ready for next modal */
const closedModal = () => {
  modalNew.value = modalEdit.value = modalDetail.value = false;
};

onMounted(async () => {
  entitySchema.value = schema.getFieldMeta('user');
  schemaTypes.value = schema.getModelEnums('user');
  await loadPendingUsers(true);
});
</script>

<style scoped>
.pointer { cursor: pointer; }
.action { height: 20px; width: auto; }
</style>
